import { ISettingsChangeData } from "../../models/event/ISettingsChangeData";
import { CursorState } from "../../states/CursorState";
import { EventManager } from "../managers/EventManager";
import { StringContext } from "../StringContext";
import { StringData } from "../StringData";
import { StringToolsContainer } from "../StringToolsContainer";

/**
 * Manages virtual cursor logic: smoothing, updating, and syncing with mouse events.
 *
 * This controller handles cursor position tracking with smoothing (lerp) logic.
 * Useful for animated cursor effects and interaction modules.
 */
export class CursorController {
  /** Context providing access to shared data, tools, and settings. */
  protected context: StringContext;

  /** Threshold below which cursor is considered settled (no movement). */
  private readonly SETTLE_THRESHOLD = 0.1;

  /** Smoothing factor used to interpolate cursor movement. */
  private smoothingFactor: number;

  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private lastMouseTime: number = 0;

  /**
   * Constructs a new `CursorController` instance.
   * @param smoothing The initial lerp smoothing factor (0 to 1).
   * @param context The shared context containing state and tools.
   */
  constructor(smoothing: number = 0.1, context: StringContext) {
    this.smoothingFactor = smoothing;
    this.context = context;

    this.onSettingsChange({
      isDesktop: context.data.viewport.windowWidth > 1024,
      isForceRebuild: false,
      widthChanged: true,
      heightChanged: true,
      scrollHeightChanged: true,
    });
  }

  /**
   * Updates the target cursor position from a mouse event.
   * This is the raw position that smoothing will interpolate toward.
   * @param e MouseEvent with current cursor position.
   */
  public onMouseMove(e: MouseEvent): void {
    this.context.data.cursor.targetX = e.clientX;
    this.context.data.cursor.targetY = e.clientY;

    const now = performance.now();
    const dt = now - this.lastMouseTime;

    if (dt > 0) {
      this.context.data.cursor.velocityX = (e.clientX - this.lastMouseX) / dt;
      this.context.data.cursor.velocityY = (e.clientY - this.lastMouseY) / dt;
    }

    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
    this.lastMouseTime = now;
  }

  /**
   * Updates smoothed cursor position using linear interpolation (lerp).
   * Should be called on every animation frame.
   * Handles snapping when movement is below threshold.
   */
  public onFrame(): void {
    const { targetX, targetY, smoothedX, smoothedY } = this.context.data.cursor;

    const stepX = this.context.tools.lerp.process({
      from: smoothedX,
      to: targetX,
      progress: this.smoothingFactor,
    });
    const stepY = this.context.tools.lerp.process({
      from: smoothedY,
      to: targetY,
      progress: this.smoothingFactor,
    });

    const distance = this.getStepDistance(stepX, stepY);

    if (this.isSettled(distance)) {
      this.snapToTarget();
    } else {
      this.applyStep(stepX, stepY);
    }
  }

  /**
   * Called when global settings change.
   * Updates the internal lerp factor from context settings.
   */
  public onSettingsChange(data: ISettingsChangeData): void {
    let lerp = Number(this.context.settings["cursor-lerp"]);
    this.setLerpFactor(lerp);
  }

  /**
   * Dynamically adjusts the smoothing factor using adaptive mapping.
   * @param t The raw input lerp value (usually from 0 to 1).
   */
  public setLerpFactor(t: number): void {
    this.smoothingFactor = this.context.tools.adaptiveLerp.process({
      value: t,
      inMin: 0.1,
      inMax: 1.0,
      outMin: 0.05,
      outMax: 0.65,
    });
  }

  /**
   * Calculates the Euclidean distance from the cursor step.
   * @param x Step in X direction.
   * @param y Step in Y direction.
   * @returns The length of the movement vector.
   */
  private getStepDistance(x: number, y: number): number {
    return Math.hypot(x, y);
  }

  /**
   * Determines whether the movement is below the settle threshold.
   * @param distance Distance between smoothed and target positions.
   * @returns Whether the cursor should snap to target.
   */
  private isSettled(distance: number): boolean {
    return distance < this.SETTLE_THRESHOLD;
  }

  /**
   * Immediately sets smoothed position to the target and zeroes deltas.
   */
  private snapToTarget(): void {
    this.context.data.cursor.smoothedX = this.context.data.cursor.targetX;
    this.context.data.cursor.smoothedY = this.context.data.cursor.targetY;
    this.context.data.cursor.stepX = 0;
    this.context.data.cursor.stepY = 0;
  }

  /**
   * Applies lerped movement step to smoothed position and stores delta.
   * @param x Step in X direction.
   * @param y Step in Y direction.
   */
  private applyStep(x: number, y: number): void {
    this.context.data.cursor.smoothedX += x;
    this.context.data.cursor.smoothedY += y;
    this.context.data.cursor.stepX = x;
    this.context.data.cursor.stepY = y;
  }
}
